import type { Request, TabState, SCData, Touchpoint, TouchpointSCData } from '../types';
// ALL_DATA no longer used - kept for reference only
// import { ALL_DATA } from '../data/requests';
import { median, avgArr } from './format';
import { validReqAges, isReqAged, splitUserSCNames, dedupeByOppId } from './opportunities';
import { hasDateRange, matchesDateRange } from './dateRange';

// Placeholder for legacy ALL_DATA references (replaced by live data in components)
const ALL_DATA: Request[] = [];

// ─── Sub-region multi-select helpers ──────────────────────────────────────
// `sub` is a multi-select array. An empty array or one containing 'All' means
// "no sub-region filter" (i.e. the whole region). Otherwise a record matches
// when its sub-region is one of the selected values (union / OR semantics).
export function isAllSub(sub: string[]): boolean {
  return !sub || sub.length === 0 || sub.includes('All');
}

export function matchesSub(sub: string[], value: string | null | undefined): boolean {
  return isAllSub(sub) || (value != null && sub.includes(value));
}

// True when a concrete sub-region filter is active (not just "All").
export function hasSubFilter(sub: string[]): boolean {
  return !isAllSub(sub);
}

// Human-readable summary of the selected sub-regions for headings/crumbs:
// 1 → its name, 2 → "A, B", 3+ → "A +N". Returns '' when no filter is active.
export function formatSub(sub: string[]): string {
  const sel = (sub || []).filter(s => s && s !== 'All');
  if (sel.length === 0) return '';
  if (sel.length <= 2) return sel.join(', ');
  return `${sel[0]} +${sel.length - 1}`;
}

// ─── Quarter multi-select helpers ─────────────────────────────────────────
// `q` is a multi-select array with the same ['All'] / [] = "no filter" sentinel
// semantics as `sub`. Otherwise a record matches when its quarter is one of the
// selected values (union / OR semantics).
export function isAllQ(q: string[]): boolean {
  return !q || q.length === 0 || q.includes('All');
}

export function matchesQ(q: string[], value: string | null | undefined): boolean {
  return isAllQ(q) || (value != null && q.includes(value));
}

// ─── Fiscal-year multi-select helpers (same ['All'] / [] sentinel as `sub` / `q`) ──
export function isAllFY(fy: string[]): boolean {
  return !fy || fy.length === 0 || fy.includes('All');
}

export function matchesFY(fy: string[], value: string | null | undefined): boolean {
  return isAllFY(fy) || (value != null && fy.includes(value));
}

export function matchesCQFilter(r: Request, s: TabState): boolean {
  // A created-date range replaces the Created Quarter filter (they are mutually exclusive).
  if (hasDateRange(s)) return matchesDateRange(r.req_entry, s);
  if (isAllFY(s.fy) && isAllQ(s.q)) return true;
  const yr = r.req_created_fy || '';
  const qt = r.req_created_q || '';
  if (!matchesFY(s.fy, yr)) return false;
  if (!matchesQ(s.q, qt)) return false;
  return true;
}

export function getFiltered(tab: 'vp' | 'sd' | 'od', s: TabState, data: Request[] = ALL_DATA): Request[] {
  return data.filter(r => {
    if (s.r && s.r !== 'Global' && r.opp_region !== s.r) return false;
    if (!matchesSub(s.sub, r.req_sub_region)) return false;
    if (tab === 'vp' && s.status && s.status !== 'All' && r.req_status !== s.status) return false;
    if (!matchesCQFilter(r, s)) return false;
    return true;
  });
}

export function buildSCList(region: string, sub: string[], cqState: TabState, data: Request[] = ALL_DATA): SCData[] {
  const scMap: Record<string, { requests: Request[]; closedRequests: Request[]; onhold: number; cancelled: number }> = {};

  data.forEach(r => {
    if (region !== 'Global' && r.opp_region !== region) return;
    if (!matchesSub(sub, r.req_sub_region)) return;
    if (!matchesCQFilter(r, cqState)) return;
    if (!r.user_sc_name || !r.user_sc_name.trim()) return; // blank SC is not counted

    for (const scKey of splitUserSCNames(r.user_sc_name)) {
      if (!scMap[scKey]) scMap[scKey] = { requests: [], closedRequests: [], onhold: 0, cancelled: 0 };
      if (r.req_status === 'In Progress') scMap[scKey].requests.push(r);
      else if (r.req_status === 'Closed') scMap[scKey].closedRequests.push(r);
      else if (r.req_status === 'On Hold') scMap[scKey].onhold++;
      else if (r.req_status === 'Cancelled') scMap[scKey].cancelled++;
    }
  });

  return Object.entries(scMap)
    .map(([sc, v]) => {
      const ages = validReqAges(v.closedRequests);
      const aged = v.requests.filter(r => isReqAged(r));
      const uniqueRequests = dedupeByOppId(v.requests);
      const uniqueClosed = dedupeByOppId(v.closedRequests);
      const gnarr = uniqueRequests.reduce((s, r) => s + r.opp_gnarr, 0);
      const closedGnarr = uniqueClosed.reduce((s, r) => s + r.opp_gnarr, 0);
      return {
        n: sc,
        ip: v.requests.length,
        avgAge: avgArr(ages),
        med: median(ages),
        gnarr,
        closedGnarr,
        aged: aged.length,
        riskGnarr: aged.reduce((s, r) => s + r.opp_gnarr, 0),
        requests: v.requests,
        closedRequests: v.closedRequests,
        closed: v.closedRequests.length,
        onhold: v.onhold,
      };
    })
    .sort((a, b) => (b.ip + b.closed) - (a.ip + a.closed));
}

export function matchesTouchpointFilter(t: Touchpoint, s: TabState): boolean {
  if (hasDateRange(s)) return matchesDateRange(t.tp_entry, s);
  if (isAllFY(s.fy) && isAllQ(s.q)) return true;
  const yr = t.tp_created_fy || '';
  const qt = t.tp_created_q || '';
  if (!matchesFY(s.fy, yr)) return false;
  if (!matchesQ(s.q, qt)) return false;
  return true;
}

export function buildTouchpointSCList(region: string, sub: string[], cqState: TabState, data: Touchpoint[] = []): TouchpointSCData[] {
  const scMap: Record<string, Touchpoint[]> = {};

  data.forEach(t => {
    if (region !== 'Global' && t.opp_region !== region) return;
    if (!matchesSub(sub, t.opp_sub_region)) return;
    if (!matchesTouchpointFilter(t, cqState)) return;

    const scNames = splitUserSCNames(t.user_sc_name);
    for (const scKey of scNames) {
      if (!scMap[scKey]) scMap[scKey] = [];
      scMap[scKey].push(t);
    }
  });

  return Object.entries(scMap)
    .map(([sc, touchpoints]) => ({
      n: sc,
      touchpoints: [...touchpoints].sort((a, b) => {
        if (!a.tp_entry) return 1;
        if (!b.tp_entry) return -1;
        return b.tp_entry.localeCompare(a.tp_entry);
      }),
      count: touchpoints.length,
    }))
    .sort((a, b) => b.count - a.count);
}

export function getFYOptions(data: Request[] = ALL_DATA): string[] {
  const fySet = new Set(data.map(r => r.req_created_fy).filter(Boolean));
  return ['All', ...Array.from(fySet).sort()];
}

// Most recent fiscal year present in the data, or null when there's no year data yet
// (e.g. before live data has loaded). Used to auto-select the FY filter's default value.
export function getLatestFY(data: Request[] = ALL_DATA): string | null {
  const years = getFYOptions(data).filter(y => y !== 'All');
  return years.length > 0 ? years[years.length - 1] : null;
}

export const PRODUCT_BUCKETS = [
  { label: 'AEM Assets', test: (p: string) => /(aem.*assets|assets.*aem)/i.test(p) },
  { label: 'Analytics', test: (p: string) => /(d&i.*analytics|analytics)/i.test(p) && !/cja/i.test(p) },
  { label: 'CJA', test: (p: string) => /cja/i.test(p) },
  { label: 'Campaign', test: (p: string) => /campaign/i.test(p) },
  { label: 'Commerce', test: (p: string) => /commerce/i.test(p) },
  { label: 'Marketo', test: (p: string) => /(marketo|measure)/i.test(p) },
  { label: 'Target', test: (p: string) => /target/i.test(p) },
  { label: 'Workfront', test: (p: string) => /(workfront|wkft)/i.test(p) },
];

export function bucketProducts(productsStr: string): Set<string> {
  const bucketHits = new Set<string>();
  const parts = productsStr.split(',').map(s => s.trim());
  for (const part of parts) {
    let matched = false;
    for (const b of PRODUCT_BUCKETS) {
      if (b.test(part)) { bucketHits.add(b.label); matched = true; }
    }
    if (!matched) bucketHits.add('Other');
  }
  return bucketHits;
}
